import React from 'react'
import axios from 'axios'
import {
  APIProvider,
  AdvancedMarker,
  InfoWindow,
  Map as GoogleMap,
  Pin,
  useMap,
} from '@vis.gl/react-google-maps'
import {
  MdHome,
  MdLocationOn,
  MdMusicNote,
  MdPerson,
} from 'react-icons/md'
import { getRequestHeaders } from '../api'
import { baseUrl } from '../assets/Animations'
import {
  formatMapAddress,
  getLocationFromRecord,
  isValidMapLocation,
} from '../StudentOnboardingCardModalFall2023/MagicScheduler/magicSchedulerUtils'
import {
  buildWaitlistFamilyGroups,
  getMatchingWaitlistTeachers,
  getTeacherInstrumentNames,
  getWaitlistInstrumentOptions,
  getWaitlistMapLocations,
} from './waitlistMapUtils'
import './waitlist-map.css'

const GOOGLE_MAPS_API_KEY = 'AIzaSyBLfMu_7gTWuHw6C3kOqLB8uBUjP9eNLF8'
const GOOGLE_MAP_ID = '3324feda48bc0f19'
const DEFAULT_MAP_CENTER = { lat: 39.5, lng: -98.35 }
const locationRequestCache = new Map()

const getTeacherAddress = (teacher) => teacher?.Address || teacher?.address || {}

const getTeacherId = (teacher, index) => String(
  teacher?.TeacherID || teacher?.teacherId || teacher?._id || `teacher-${index}`
)

const resolveAddress = async (address) => {
  const normalizedAddress = String(address || '').trim()
  if (!normalizedAddress) return null
  const cacheKey = normalizedAddress.toLowerCase()
  if (locationRequestCache.has(cacheKey)) return locationRequestCache.get(cacheKey)

  const request = axios.post(
    `${baseUrl}/scheduler-geocodes/resolve`,
    { address: normalizedAddress },
    getRequestHeaders()
  ).then((response) => {
    const location = {
      lat: Number(response?.data?.lat),
      lng: Number(response?.data?.lng),
    }
    return isValidMapLocation(location) ? location : null
  }).catch((error) => {
    console.error('Unable to resolve waitlist map address:', error)
    return null
  })

  locationRequestCache.set(cacheKey, request)
  return request
}

const WaitlistMapCamera = ({ markers, selectedMarker }) => {
  const map = useMap()
  const markerKey = markers
    .map((marker) => `${marker.id}:${marker.location.lat}:${marker.location.lng}`)
    .join('|')

  React.useEffect(() => {
    if (!map || markers.length === 0 || !window.google?.maps?.LatLngBounds) return
    const bounds = new window.google.maps.LatLngBounds()
    markers.forEach((marker) => bounds.extend(marker.location))

    if (markers.length === 1) {
      map.setCenter(markers[0].location)
      map.setZoom(11)
    } else {
      map.fitBounds(bounds, 70)
    }
    // markerKey represents every coordinate that affects the bounds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, markerKey])

  React.useEffect(() => {
    if (!map || !selectedMarker) return
    map.panTo(selectedMarker.location)
  }, [map, selectedMarker])

  return null
}

const WaitlistMap = ({ summary, teachers = [] }) => {
  const locationOptions = React.useMemo(
    () => getWaitlistMapLocations(summary),
    [summary]
  )
  const [selectedLocationId, setSelectedLocationId] = React.useState('')
  const instrumentOptions = React.useMemo(
    () => getWaitlistInstrumentOptions(summary, selectedLocationId || 'all'),
    [selectedLocationId, summary]
  )
  const [selectedInstrumentId, setSelectedInstrumentId] = React.useState('')
  const [markers, setMarkers] = React.useState([])
  const [selectedMarkerId, setSelectedMarkerId] = React.useState('')
  const [loadingMarkers, setLoadingMarkers] = React.useState(false)
  const [mapMessage, setMapMessage] = React.useState('')

  React.useEffect(() => {
    if (locationOptions.length === 0) {
      setSelectedLocationId('')
      return
    }
    if (
      selectedLocationId !== 'all'
      && !locationOptions.some((location) => location.id === selectedLocationId)
    ) {
      setSelectedLocationId(locationOptions[0].id)
    }
  }, [locationOptions, selectedLocationId])

  React.useEffect(() => {
    if (instrumentOptions.length === 0) {
      setSelectedInstrumentId('')
      return
    }
    if (!instrumentOptions.some((instrument) => instrument.id === selectedInstrumentId)) {
      setSelectedInstrumentId(instrumentOptions[0].id)
    }
  }, [instrumentOptions, selectedInstrumentId])

  const selectedInstrument = instrumentOptions.find(
    (instrument) => instrument.id === selectedInstrumentId
  )

  const familyGroups = React.useMemo(
    () => buildWaitlistFamilyGroups({
      summary,
      instrumentName: selectedInstrument?.label,
      locationId: selectedLocationId,
    }),
    [selectedInstrument?.label, selectedLocationId, summary]
  )
  const matchingTeachers = React.useMemo(
    () => getMatchingWaitlistTeachers({
      teachers,
      instrumentName: selectedInstrument?.label,
      familyGroups,
      locationId: selectedLocationId,
    }),
    [familyGroups, selectedInstrument?.label, selectedLocationId, teachers]
  )

  React.useEffect(() => {
    if (!selectedInstrument?.label) {
      setMarkers([])
      setMapMessage('No listed instruments were found for this school location.')
      setLoadingMarkers(false)
      return undefined
    }

    let isActive = true
    const loadMarkers = async () => {
      setLoadingMarkers(true)
      setMapMessage('')
      setSelectedMarkerId('')

      const familyMarkerResults = await Promise.all(
        familyGroups.map(async (family) => {
          const address = formatMapAddress(family.familyAddress)
          const savedLocation = getLocationFromRecord(family.familyLocation || {})
            || getLocationFromRecord(family.familyAddress || {})
          const location = isValidMapLocation(savedLocation)
            ? savedLocation
            : await resolveAddress(address)
          if (!isValidMapLocation(location)) return null

          return {
            id: `family-${family.id}`,
            type: 'family',
            familyId: family.familyId,
            name: family.familyName,
            address,
            location,
            schoolName: family.schoolName,
            students: family.students,
          }
        })
      )

      const teacherMarkerResults = await Promise.all(
        matchingTeachers.map(async (teacher, index) => {
          const addressRecord = getTeacherAddress(teacher)
          const address = formatMapAddress(addressRecord)
          const savedLocation = getLocationFromRecord(teacher)
            || getLocationFromRecord(addressRecord)
          const location = isValidMapLocation(savedLocation)
            ? savedLocation
            : await resolveAddress(address)
          if (!isValidMapLocation(location)) return null

          return {
            id: `teacher-${getTeacherId(teacher, index)}`,
            type: 'teacher',
            teacherId: getTeacherId(teacher, index),
            name: teacher?.Name || teacher?.name || 'Teacher',
            address,
            location,
            schoolName: teacher?.School || teacher?.school || '',
            instruments: getTeacherInstrumentNames(teacher),
            wantsMoreStudents: teacher?.WantsMoreStudents !== false,
          }
        })
      )

      if (!isActive) return
      const nextMarkers = [...familyMarkerResults, ...teacherMarkerResults].filter(Boolean)
      const missingCount = familyGroups.length + matchingTeachers.length - nextMarkers.length
      setMarkers(nextMarkers)
      setMapMessage(
        nextMarkers.length === 0
          ? 'No mappable addresses were found for this instrument and location.'
          : missingCount > 0
            ? `${missingCount} ${missingCount === 1 ? 'address was' : 'addresses were'} missing or could not be mapped.`
            : ''
      )
      setLoadingMarkers(false)
    }

    loadMarkers()
    return () => {
      isActive = false
    }
  }, [familyGroups, matchingTeachers, selectedInstrument?.label])

  const selectedMarker = markers.find((marker) => marker.id === selectedMarkerId) || null
  const totalWaitlistStudents = familyGroups.reduce(
    (total, family) => total + family.students.length,
    0
  )
  const markerIds = new Set(markers.map((marker) => marker.id))

  if (locationOptions.length === 0) {
    return (
      <section className='waitlist-map waitlist-map--empty'>
        <MdMusicNote size={28} aria-hidden='true' />
        <strong>No waitlist students yet</strong>
        <span>Add a student to a waitlist to use this view.</span>
      </section>
    )
  }

  return (
    <section className='waitlist-map' aria-label='Waitlist students and matching teachers map'>
      <aside className='waitlist-map__sidebar'>
        <div className='waitlist-map__controls'>
          <label htmlFor='waitlist-map-location'>School location</label>
          <select
            id='waitlist-map-location'
            value={selectedLocationId}
            onChange={(event) => setSelectedLocationId(event.target.value)}
          >
            {locationOptions.map((location) => (
              <option key={location.id} value={location.id}>{location.label}</option>
            ))}
            <option value='all'>All school locations</option>
          </select>

          <label htmlFor='waitlist-map-instrument'>Instrument</label>
          <select
            id='waitlist-map-instrument'
            value={selectedInstrumentId}
            onChange={(event) => setSelectedInstrumentId(event.target.value)}
            disabled={instrumentOptions.length === 0}
          >
            {instrumentOptions.length === 0 && (
              <option value=''>No instruments listed</option>
            )}
            {instrumentOptions.map((instrument) => (
              <option key={instrument.id} value={instrument.id}>{instrument.label}</option>
            ))}
          </select>
        </div>

        <div className='waitlist-map__legend'>
          <span><i className='is-family' /> Waitlist families</span>
          <span><i className='is-teacher' /> Matching active teachers</span>
        </div>

        <div className='waitlist-map__counts'>
          <div><strong>{totalWaitlistStudents}</strong><span>students</span></div>
          <div><strong>{familyGroups.length}</strong><span>homes</span></div>
          <div><strong>{matchingTeachers.length}</strong><span>teachers</span></div>
        </div>

        <div className='waitlist-map__lists'>
          <div className='waitlist-map__list-section'>
            <h3><MdPerson size={16} aria-hidden='true' /> Teachers</h3>
            {!selectedInstrument?.label ? (
              <p>Select a school location with a listed instrument.</p>
            ) : matchingTeachers.length === 0 ? (
              <p>No active teachers at the selected location teach {selectedInstrument?.label}.</p>
            ) : matchingTeachers.map((teacher, index) => {
              const markerId = `teacher-${getTeacherId(teacher, index)}`
              return (
                <button
                  type='button'
                  key={markerId}
                  className={selectedMarkerId === markerId ? 'is-selected' : ''}
                  disabled={!markerIds.has(markerId)}
                  onClick={() => setSelectedMarkerId(markerId)}
                >
                  <span className='waitlist-map__list-icon is-teacher'><MdPerson size={15} /></span>
                  <span><strong>{teacher?.Name || 'Teacher'}</strong><small>{teacher?.School?.toUpperCase?.() || ''}</small></span>
                </button>
              )
            })}
          </div>

          <div className='waitlist-map__list-section'>
            <h3><MdHome size={16} aria-hidden='true' /> Waitlist homes</h3>
            {familyGroups.map((family) => {
              const markerId = `family-${family.id}`
              return (
                <button
                  type='button'
                  key={markerId}
                  className={selectedMarkerId === markerId ? 'is-selected' : ''}
                  disabled={!markerIds.has(markerId)}
                  onClick={() => setSelectedMarkerId(markerId)}
                >
                  <span className='waitlist-map__list-icon is-family'><MdHome size={15} /></span>
                  <span>
                    <strong>{family.familyName} family</strong>
                    <small>{family.students.map((student) => student.studentName).join(', ')}</small>
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </aside>

      <div className='waitlist-map__map-shell'>
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            style={{ width: '100%', height: '100%' }}
            defaultCenter={DEFAULT_MAP_CENTER}
            defaultZoom={4}
            gestureHandling='greedy'
            disableDefaultUI={false}
            mapId={GOOGLE_MAP_ID}
          >
            <WaitlistMapCamera markers={markers} selectedMarker={selectedMarker} />
            {markers.map((marker) => (
              <AdvancedMarker
                key={marker.id}
                position={marker.location}
                title={marker.type === 'teacher'
                  ? `${marker.name} — matching teacher`
                  : `${marker.name} family — waitlist`}
                onClick={() => setSelectedMarkerId(marker.id)}
              >
                <Pin
                  background={marker.type === 'teacher' ? '#F97316' : '#7C3AED'}
                  borderColor={marker.type === 'teacher' ? '#9A3412' : '#4C1D95'}
                  glyphColor='#FFFFFF'
                  glyph={marker.type === 'teacher' ? 'T' : 'W'}
                  scale={selectedMarkerId === marker.id ? 1.3 : 1.05}
                />
              </AdvancedMarker>
            ))}

            {selectedMarker && (
              <InfoWindow
                position={selectedMarker.location}
                onCloseClick={() => setSelectedMarkerId('')}
              >
                <div className='waitlist-map__info'>
                  <strong>
                    {selectedMarker.type === 'teacher'
                      ? selectedMarker.name
                      : `${selectedMarker.name} family`}
                  </strong>
                  <span className='waitlist-map__info-type'>
                    {selectedMarker.type === 'teacher' ? 'Matching teacher' : 'Waitlist family'}
                  </span>
                  {selectedMarker.address && (
                    <span><MdLocationOn size={13} /> {selectedMarker.address}</span>
                  )}
                  {selectedMarker.type === 'teacher' ? (
                    <>
                      <div className='waitlist-map__info-chips'>
                        {selectedMarker.instruments.map((instrument) => (
                          <i key={instrument}>{instrument}</i>
                        ))}
                      </div>
                      <small>
                        {selectedMarker.wantsMoreStudents
                          ? 'Accepting more students'
                          : 'Currently marked full'}
                      </small>
                    </>
                  ) : (
                    <div className='waitlist-map__info-students'>
                      {selectedMarker.students.map((student) => (
                        <span key={student.studentId}>{student.studentName} · {selectedInstrument?.label}</span>
                      ))}
                    </div>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </APIProvider>

        {loadingMarkers && (
          <div className='waitlist-map__status is-loading'>
            <span className='waitlist-summary__spinner' />
            Mapping students and teachers…
          </div>
        )}
        {!loadingMarkers && mapMessage && (
          <div className={`waitlist-map__status ${markers.length === 0 ? 'is-error' : 'is-notice'}`}>
            {mapMessage}
          </div>
        )}
        <div className='waitlist-map__cache-note'>Locations use the saved scheduler map cache.</div>
      </div>
    </section>
  )
}

export default WaitlistMap
